import { Badge, Box, Button, Collapse, Divider, Flex, Heading, SimpleGrid, Text } from "@chakra-ui/react";
import { useState } from "react";
import { FiArrowRight, FiInfo, FiMapPin } from "react-icons/fi";
import { formatCep } from "../../../utils/formatCep";
import { formatCnpj } from "../../../utils/formatCnpj";
import type { Entrega } from "../utils/agruparEntregas";
import CelularClienteButton from "./CelularClienteButton";
import DadosComplementaresButton from "./DadosComplementaresButton";
import NotasFiscaisDaEntrega from "./NotasFiscaisDaEntrega";
import RotaButton from "./RotaButton";
import VerAgendamentoButton from "./VerAgendamentoButton";

interface IProps {
  entrega: Entrega;
  onEntregar: () => void;
}

const plural = (quantidade: number, singular: string, pluralTexto: string) =>
  `${quantidade} ${quantidade === 1 ? singular : pluralTexto}`;

const EntregaCard = ({ entrega, onEntregar }: IProps) => {
  const [detalhesAbertos, setDetalhesAbertos] = useState(false);
  // Com uma nota só, a nota já aparece aberta no card e o DETALHES abre direto a gaveta dela.
  const notaUnica = entrega.notas.length === 1 ? entrega.notas[0] : null;

  return (
    <Box _hover={{ backgroundColor: '#f7f7f7' }} shadow="md" bg="white" mb={1}>
      <Box width="full" p={3}>
        <Heading size="md">{entrega.fantasias}</Heading>
        {entrega.tem_reentrega && <Badge colorScheme="green" mr={1}>REENTREGA</Badge>}
        {entrega.tem_nota_pallet && <Badge colorScheme="yellow" mr={1}>NOTA PALLET</Badge>}

        <SimpleGrid columns={2} fontSize="0.9rem" mt={1}>
          <Box pr={1}>
            {entrega.celulares.map((celular) => (
              <CelularClienteButton key={celular} celular={celular} />
            ))}
            <small>Endereço: {entrega.endereco}, n° {entrega.numero} - {entrega.bairro}</small> <br />
            <Flex alignItems="center" gap={1} wrap="wrap">
              <FiMapPin color="#2b6cb0" />
              <small><b>Cidade:</b> {entrega.cidade} - {entrega.uf} ({formatCep(entrega.cep)})</small>
            </Flex>
            {entrega.latitude && entrega.longitude &&
              <RotaButton margin={false} latitude={entrega.latitude} longitude={entrega.longitude} />
            }
          </Box>
          <Box>
            <small>Razão Social: {entrega.razao_social}</small> <br />
            <small>CNPJ: {formatCnpj(entrega.cnpj)}</small> <br />
            {notaUnica
              ? <><small>Pedido: {notaUnica.pedido_numero}</small> <br /></>
              : <><small>Pedidos: {entrega.pedidos.length}</small> <br /></>}
            {entrega.tipos_pagamento.length > 0 &&
              <small style={{ color: 'red' }}>Tipo de Pagamento: {entrega.tipos_pagamento.join(', ')}</small>
            }
          </Box>
        </SimpleGrid>

        {notaUnica && (
          <VerAgendamentoButton
            mt={1}
            j14_ukey={notaUnica.j14_ukey}
            j07_ukey={notaUnica.j07_ukey}
            pedido_numero={notaUnica.pedido_numero}
            pedido_status_agendamento={notaUnica.pedido_status_agendamento}
          />
        )}

        <NotasFiscaisDaEntrega entrega={entrega}>
          {!notaUnica && <Flex alignItems="center" gap={2} mt={1} wrap="wrap">
            <FiInfo color="#2b6cb0" />
            <Text>
              Esta entrega possui {plural(entrega.notas.length, 'nota fiscal', 'notas fiscais')}.
            </Text>
            <Button variant="link" size="sm" colorScheme="blue" onClick={() => setDetalhesAbertos(!detalhesAbertos)}>
              {detalhesAbertos ? 'Ocultar detalhes' : 'Ver detalhes'}
            </Button>
          </Flex>}
        </NotasFiscaisDaEntrega>

        {!notaUnica && <Collapse in={detalhesAbertos} animateOpacity>
          <Box mt={2}>
            {entrega.notas.map((nota, indice) => (
              <Box key={nota.j10_ukey}>
                {indice > 0 && <Divider my={2} />}
                <Flex justifyContent="space-between" alignItems="center" wrap="wrap" gap={2}>
                  <Box fontSize="0.9rem">
                    <Text fontWeight="bold">Nota Fiscal: {nota.nota_numero}</Text>
                    {nota.e_reentrega == '1' && <Badge colorScheme="green" mr={1}>REENTREGA</Badge>}
                    {nota.e_nota_pallet == '1' && <Badge colorScheme="yellow" mr={1}>NOTA PALLET</Badge>}
                    <small>Fantasia: {nota.cliente_fantasia}</small> <br />
                    <small>Pedido: {nota.pedido_numero}</small> <br />
                    {!!nota.pedido_tipo_documento_nome && <small style={{ color: 'red' }}>Tipo de Pagamento: {nota.pedido_tipo_documento_nome}</small>}
                    {(!!nota.pedido_motivo_da_troca && nota.pedido_motivo_da_troca == 'QUALIDADE') &&
                      <><br /><small style={{ color: 'red' }}>Motivo da Troca: {nota.pedido_motivo_da_troca}</small></>
                    }
                  </Box>
                  <Flex direction="column" gap={1} alignItems="flex-end">
                    <DadosComplementaresButton
                      label={`Detalhes NF ${nota.nota_numero}`}
                      vendedor={{ vendedor_codigo: nota.vendedor_codigo, vendedor_nome: nota.vendedor_nome, vendedor_celular: nota.vendedor_celular }}
                      dados_complementares={nota}
                      pessoa={nota.cliente_razao_social}
                      produtos={nota.produtos}
                    />
                    <VerAgendamentoButton
                      j14_ukey={nota.j14_ukey}
                      j07_ukey={nota.j07_ukey}
                      pedido_numero={nota.pedido_numero}
                      pedido_status_agendamento={nota.pedido_status_agendamento}
                    />
                  </Flex>
                </Flex>
              </Box>
            ))}
          </Box>
        </Collapse>}

        <Flex width="full" justifyContent="space-between" mt={2} flexWrap="wrap" gap={2}>
          {notaUnica
            ? <DadosComplementaresButton
                vendedor={{ vendedor_codigo: notaUnica.vendedor_codigo, vendedor_nome: notaUnica.vendedor_nome, vendedor_celular: notaUnica.vendedor_celular }}
                dados_complementares={notaUnica}
                pessoa={notaUnica.cliente_razao_social}
                produtos={notaUnica.produtos}
              />
            : <Button size="sm" colorScheme="blue" onClick={() => setDetalhesAbertos(!detalhesAbertos)}>
                <FiInfo /> &nbsp; DETALHES
              </Button>}
          <Button onClick={onEntregar} size="sm" colorScheme="green"><Text mr={1}>ENTREGAR</Text> <FiArrowRight fontSize="1.3rem" /></Button>
        </Flex>
      </Box>
    </Box>
  )
}

export default EntregaCard;
